import React, { useState } from 'react';
import { Input } from 'antd';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import { CONTACTED, CONTACTED_AO3, CONTACTED_RR, SOURCE } from '../../../core/constants';
import Endpoints from '../../../core/constants/endpoints';
import { IBook } from '../../../section/Common/types/types';
import { useActions } from '../../../context/Actions.context';
import { useServices } from '../../../core/services/useServices';
import showActionNotification from '../../../section/Common/utils/showActionNotification';

const { TextArea } = Input;

interface IProps {
  book: IBook | Record<string, string | number>;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const createContactedObject = (source: string, book: IBook | Record<string, string | number>) => {
  if (source === SOURCE.bookstat) {
    return { amazonasin: book.ASIN };
  } else if (source === SOURCE.ao3) {
    return { work_id: book.work_id };
  }
  else {
    return { fiction_id: book['Fiction Id'] };
  }
};

const getContactedType = (source: string) => {
  if (source === SOURCE.bookstat) {
    return CONTACTED;
  } else if (source === SOURCE.ao3) {
    return CONTACTED_AO3;
  }
  else {
    return CONTACTED_RR;
  }
};

const ContactedTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { source, isActionLoading, setActionLoading } = useActions();
  const { book: bookService } = useServices();
  const queryClient = useQueryClient();
  const history = useHistory();
  const contacted = createContactedObject(source['source'], book);
  const contactedType = getContactedType(source['source']);
  const [notes, setNotes] = useState<string>('');

  const { mutateAsync: mutateContacted } = useUpdateTitle(contactedType);

  const handleClose = () => {
    setNotes('');
    toggleModal();
  };

  const onSend = () => {
    setActionLoading(true);

    const body = {
      ...(notes.trim() && { notes: notes.trim() })
    };

    const capturedSource = source['source'];
    const capturedParams = { ...contacted };

    const promise = mutateContacted({ params: contacted, body });

    promise.then(res => {
      if (res?.status === 200) {
        showActionNotification({
          type: 'success',
          message: 'The Title was successfully sent to the Contacted tab.',
          destination: {
            name: 'Contacted',
            onGoTo: () => history.push(`${capturedSource}/contacted`)
          },
          onUndo: async () => {
            await bookService.updateTitle(
              {
                source: capturedSource,
                sourceTab: 'contacted',
                endpoint: Endpoints[id],
                action: id
              },
              capturedParams
            );
          },
          queryClient,
          id
        });
      } else {
        showActionNotification({
          type: 'error',
          message: 'The Title has not been sent to the Contacted tab.'
        });
      }
    }).catch(() => {
      showActionNotification({
        type: 'error',
        message: 'The Title has not been sent to the Contacted tab.'
      });
    });

    promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
    promise.then(() => queryClient.refetchQueries(`get-${id}`));
    promise.finally(() => setActionLoading(false));

    handleClose();
  };

  return (
    <Modal
      okText={'Send To Contacted'}
      title='Send To Contacted'
      visible={modal}
      onOk={onSend}
      confirmLoading={isActionLoading}
      onCancel={handleClose}
    >
      <div>
        <span>
          Help yourself and others understand why you&apos;re sending this. Leave a note, or skip and
          continue.
        </span>
        <br />
        <br />
        <TextArea
          placeholder='Add your notes here...'
          maxLength={400}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />

        <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
          {notes.length} / 400
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(ContactedTitles);
